import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext } from 'react';

import FullLayout from 'layout/FullLayout';
import CardMenuProfile from './ComponentsPage/CardMenuProfile';
import { API_URL, get } from 'lib/network/api';

// 1. Definisikan Context
const ProfileUserContext = createContext(null);

// 2. Fetcher Function (di luar component agar tidak dibuat ulang)
const fetchUserProfile = async () => {
  const response = await get(`${API_URL}/api/user`);
  if (!response || response.error) {
    throw new Error(response?.message || 'Failed to fetch user data');
  }
  return response; // Pastikan return data inti
};

const ProfileLayout = () => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['user-profile'],
    queryFn: fetchUserProfile,
    // Agar data tidak re-fetch saat pindah tab/halaman dalam 5 menit
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false, // Opsional: mencegah refresh saat pindah tab browser
  });

  const contextValue = useMemo(() => data, [data]);

  if (isLoading) return <ProfileLoadingState />;
  if (isError) return <ProfileErrorState message={error.message} />;

  return (
    <ProfileUserContext.Provider value={contextValue}>
      <FullLayout>
        <div className="container mx-auto pt-6 pb-24 text-kin-text px-4 sm:px-0">
          <div className="flex flex-col gap-8 sm:grid sm:grid-cols-7">
            <aside className="sm:col-span-2 relative mb-6 sm:mb-0">
              <div className="sm:sticky sm:top-[90px]">
                <CardMenuProfile item={data} />
              </div>
            </aside>

            {/* Main Content */}
            <main className="sm:col-span-5">
              <Outlet />
            </main>
          </div>
        </div>
      </FullLayout>
    </ProfileUserContext.Provider>
  );
};

const ProfileLoadingState = () => (
  <FullLayout>
    <div className="container mx-auto pt-6 pb-24 px-2 sm:px-0">
      <div className="flex flex-col gap-8 sm:grid sm:grid-cols-7">
        <div className="sm:col-span-2">
          <div className="animate-pulse bg-gray-200 rounded-xl h-40 w-full" />
        </div>
        <div className="sm:col-span-5">
          <div className="animate-pulse bg-gray-200 rounded-xl h-96 w-full" />
        </div>
      </div>
    </div>
  </FullLayout>
);

const ProfileErrorState = ({ message }) => (
  <FullLayout>
    <div className="flex items-center justify-center min-h-[50vh]">
      <p className="text-red-500 font-medium">Error: {message}</p>
    </div>
  </FullLayout>
);

export const useProfileUser = () => {
  const context = useContext(ProfileUserContext);
  if (context === undefined) {
    throw new Error('useProfileUser must be used within a ProfileUserContext');
  }
  return context;
};

export default ProfileLayout;
